import { getPageSeo } from '../content';

/** Shared content for focused SEO landing pages. */

export const HOME_INDUSTRIES = [
  {
    title: 'Ecommerce & marketplaces',
    desc: 'Storefronts, Magento builds, catalog operations, checkout, and channel integrations.',
    to: '/ecommerce-development',
  },
  {
    title: 'Education & institutions',
    desc: 'Course discovery sites, campaign pages, flexible CMS sections, and form integrations.',
    to: '/education-website-development',
  },
  {
    title: 'SaaS & product teams',
    desc: 'React, Next.js, APIs, dashboards, and release-ready engineering for global products.',
    to: '/web-development',
  },
  {
    title: 'Operations & automation',
    desc: 'ERP workflows, AI assistants, CRM connections, and reporting that reduce manual work.',
    to: '/erp-development',
  },
];

export const AI_AUTOMATION_PAGE = {
  seo: getPageSeo('/ai-automation'),
  eyebrow: 'AI automation services',
  title: 'AI automation that fits your business stack',
  lead:
    'We design chatbots, voice flows, and workflow automation that connect to your CRM, forms, ecommerce, and reporting tools—so teams spend less time on repetitive work and more time on growth.',
  badges: ['LLM workflows', 'Chatbots', 'Voice assistants', 'CRM integrations'],
  services: [
    {
      title: 'AI solutions & automation',
      desc: 'LLM-powered workflows for content generation, support triage, lead routing, and operational reporting.',
    },
    {
      title: 'AI chatbot development',
      desc: 'Conversational bots for lead capture, FAQs, and handoffs with Salesforce, HubSpot, or custom APIs.',
    },
    {
      title: 'AI voice assistants',
      desc: 'Voice interfaces for enquiry routing, appointment booking, and support flows with fallback handling.',
    },
    {
      title: 'Workflow integrations',
      desc: 'Connect forms, CRM, ecommerce, email, and internal tools with guardrails and audit-friendly logging.',
    },
  ],
  process: [
    { title: 'Discover', desc: 'Map use cases, data sources, compliance needs, and success metrics.' },
    { title: 'Design', desc: 'Define prompts, intents, handoffs, guardrails, and reporting requirements.' },
    { title: 'Build', desc: 'Implement bots, automations, and integrations with testing and monitoring.' },
    { title: 'Improve', desc: 'Tune responses, expand workflows, and optimize based on real usage data.' },
  ],
  deliverables: [
    'Use-case discovery',
    'Bot and workflow design',
    'CRM and API integrations',
    'Analytics and reporting',
    'Guardrails and fallback flows',
    'Ongoing optimization',
  ],
  relatedLinks: [
    { to: '/web-development', label: 'Web development' },
    { to: '/services', label: 'All services' },
    { to: '/getintouch', label: 'Contact' },
  ],
  faqs: [
    {
      question: 'Can you integrate AI with our existing CRM or forms?',
      answer:
        'Yes. We commonly connect AI workflows to Salesforce, HubSpot, custom APIs, ecommerce platforms, and internal dashboards.',
    },
    {
      question: 'Do you build both chat and voice assistants?',
      answer:
        'Yes. We deliver text chatbots, voice interfaces, and hybrid flows depending on your customer journey and support model.',
    },
    {
      question: 'What kinds of automation do teams start with first?',
      answer:
        'Common starting points include lead qualification, support triage, FAQ deflection, internal knowledge search, order-status lookups, and CRM data enrichment.',
    },
    {
      question: 'How do you keep AI workflows safe in production?',
      answer:
        'We design guardrails, fallback paths, logging, role-based access, and human handoffs so teams can monitor quality and improve over time.',
    },
  ],
  primaryCta: { to: '/getintouch', label: 'Plan an AI use case' },
  secondaryCta: { to: '/services', label: 'Explore all services' },
};

export const WORDPRESS_DEVELOPMENT_PAGE = {
  seo: getPageSeo('/wordpress-development'),
  eyebrow: 'WordPress development',
  title: 'WordPress and Roots Sage engineering for content teams',
  lead:
    'We build maintainable WordPress platforms with Roots Sage, Blade templates, ACF-driven sections, modern asset pipelines, and performance-focused delivery for marketing and product teams worldwide.',
  badges: ['Roots Sage', 'ACF sections', 'Tailwind CSS', 'Performance tuning'],
  services: [
    {
      title: 'Custom Sage themes',
      desc: 'Component-based WordPress themes with Blade, Vite, Tailwind, and maintainable architecture.',
    },
    {
      title: 'Flexible content sections',
      desc: 'ACF-driven layouts so teams can compose heroes, tiles, CTAs, and content bands without developer bottlenecks.',
    },
    {
      title: 'Integrations',
      desc: 'Forms, CRM, search, analytics, embeds, and third-party tools wired into your publishing workflow.',
    },
    {
      title: 'Support & optimization',
      desc: 'Updates, performance tuning, accessibility improvements, and ongoing content platform evolution.',
    },
  ],
  process: [
    { title: 'Plan', desc: 'Define content models, templates, integrations, and rollout priorities.' },
    { title: 'Design', desc: 'Shape layouts, navigation, and conversion paths for your audiences.' },
    { title: 'Build', desc: 'Implement Sage themes, sections, and reusable content blocks.' },
    { title: 'Support', desc: 'Maintain performance, security, and new publishing requirements.' },
  ],
  deliverables: [
    'Custom theme development',
    'Flexible section builders',
    'Form and CRM integrations',
    'Search and discovery features',
    'Performance optimization',
    'Ongoing maintenance',
  ],
  relatedLinks: [
    { to: '/education-website-development', label: 'Education websites' },
    { to: '/website-maintenance', label: 'Website maintenance' },
    { to: '/web-development', label: 'Web development' },
  ],
  faqs: [
    {
      question: 'Do you work with Roots Sage and modern WordPress stacks?',
      answer:
        'Yes. We specialize in Roots Sage themes with Blade, Acorn, Vite, Tailwind CSS, and ACF-driven content models.',
    },
    {
      question: 'Can you support an existing WordPress site?',
      answer:
        'Yes. We can audit, refactor, optimize, and extend existing WordPress sites as well as build new platforms from scratch.',
    },
    {
      question: 'Can marketing teams publish without developer help?',
      answer:
        'Yes. We implement flexible ACF sections for heroes, tiles, CTAs, testimonials, and content bands so teams can publish safely.',
    },
    {
      question: 'Do you handle performance and security updates?',
      answer:
        'Yes. We provide ongoing maintenance, plugin updates, performance tuning, and accessibility improvements after launch.',
    },
  ],
  primaryCta: { to: '/getintouch', label: 'Discuss a WordPress project' },
};

export const WEBSITE_MAINTENANCE_PAGE = {
  seo: getPageSeo('/website-maintenance'),
  eyebrow: 'Website maintenance',
  title: 'Keep your website fast, secure, and reliable after launch',
  lead:
    'Hosting hygiene, updates, monitoring, Core Web Vitals, security patches, and ongoing improvements—so production sites stay fast, secure, and ready for traffic.',
  badges: ['Core Web Vitals', 'Hosting support', 'Backups', 'Monitoring'],
  services: [
    {
      title: 'Performance optimization',
      desc: 'Improve Core Web Vitals, rendering, assets, caching, and critical user journeys under real traffic.',
    },
    {
      title: 'Hosting & infrastructure',
      desc: 'SSL, DNS, backups, uptime discipline, deployment support, and server hygiene.',
    },
    {
      title: 'Security & updates',
      desc: 'Patch management, dependency updates, monitoring, and incident response planning.',
    },
    {
      title: 'Continuous improvements',
      desc: 'Analytics-led enhancements, bug fixes, content support, and roadmap-ready maintenance.',
    },
  ],
  process: [
    { title: 'Audit', desc: 'Review speed, hosting, security, integrations, and maintenance gaps.' },
    { title: 'Stabilize', desc: 'Fix urgent issues, backups, SSL, monitoring, and release hygiene.' },
    { title: 'Optimize', desc: 'Improve performance, workflows, and operational reliability.' },
    { title: 'Maintain', desc: 'Provide ongoing support, updates, and measurable improvements.' },
  ],
  deliverables: [
    'Performance audits',
    'Hosting and SSL support',
    'Backup and monitoring setup',
    'Security updates',
    'Bug fixes and enhancements',
    'Monthly improvement reports',
  ],
  relatedLinks: [
    { to: '/web-development', label: 'Web development' },
      { to: '/website-maintenance', label: 'Web hosting' },
    { to: '/magento-development', label: 'Magento support' },
  ],
  faqs: [
    {
      question: 'Can you maintain sites you did not originally build?',
      answer:
        'Yes. We regularly take over existing WordPress, Magento, React, and custom applications and improve their stability and performance.',
    },
    {
      question: 'Do you help with Core Web Vitals and page speed?',
      answer:
        'Yes. Performance optimization is a core part of our maintenance work, including audits, fixes, and ongoing monitoring.',
    },
    {
      question: 'What does a maintenance engagement usually include?',
      answer:
        'Typical coverage includes updates, backups, monitoring, bug fixes, SSL/hosting hygiene, security patches, and monthly improvement reports.',
    },
    {
      question: 'Can you support production incidents?',
      answer:
        'Yes. We help teams stabilize urgent issues, restore service, and put monitoring and release hygiene in place to reduce repeat incidents.',
    },
  ],
  primaryCta: { to: '/getintouch', label: 'Request a maintenance review' },
  secondaryCta: { to: '/services', label: 'Explore all services' },
};

export const ECOMMERCE_DEVELOPMENT_PAGE = {
  seo: getPageSeo('/ecommerce-development'),
  eyebrow: 'Ecommerce development',
  title: 'Ecommerce platforms built for conversion and operations',
  lead:
    'Catalog UX, checkout flows, payment and shipping integrations, and admin tooling—so your store converts better and stays manageable as you scale.',
  badges: ['Storefront UX', 'Checkout', 'Integrations', 'Operations'],
  services: [
    {
      title: 'Storefront experience',
      desc: 'Catalog navigation, product detail pages, merchandising, and conversion-focused UX.',
    },
    {
      title: 'Checkout & payments',
      desc: 'Payment gateways, shipping rules, tax logic, and order flows tuned for reliability.',
    },
    {
      title: 'Integrations',
      desc: 'ERP, CRM, inventory, marketplace, analytics, and fulfillment connections.',
    },
    {
      title: 'Operations tooling',
      desc: 'Admin workflows, reporting, automation, and support for high-volume commerce teams.',
    },
  ],
  process: [
    { title: 'Discover', desc: 'Understand products, channels, operations, and growth goals.' },
    { title: 'Design', desc: 'Plan UX, architecture, integrations, and launch milestones.' },
    { title: 'Build', desc: 'Implement storefront, admin, and connected systems.' },
    { title: 'Optimize', desc: 'Improve conversion, performance, and operational efficiency.' },
  ],
  deliverables: [
    'Custom ecommerce builds',
    'Checkout optimization',
    'Payment and shipping setup',
    'ERP and CRM integrations',
    'Admin and reporting tools',
    'Post-launch support',
  ],
  relatedLinks: [
    { to: '/magento-development', label: 'Magento development' },
    { to: '/marketplace-cataloging', label: 'Marketplace cataloging' },
    { to: '/erp-development', label: 'Commerce ERP' },
  ],
  faqs: [
    {
      question: 'Do you work with Magento and custom ecommerce stacks?',
      answer:
        'Yes. We deliver Magento/Adobe Commerce projects as well as custom ecommerce builds with modern JavaScript and API-driven architectures.',
    },
    {
      question: 'Can you improve an existing store instead of rebuilding it?',
      answer:
        'Yes. We often optimize checkout, performance, integrations, and admin workflows on live ecommerce platforms.',
    },
    {
      question: 'Which ecommerce use cases do you handle most often?',
      answer:
        'We commonly work on B2C storefronts, B2B catalogs, multi-vendor marketplaces, subscription flows, ERP-connected operations, and high-traffic checkout optimization.',
    },
    {
      question: 'Can you connect ecommerce with ERP, CRM, and marketplaces?',
      answer:
        'Yes. Integrations are a core part of our ecommerce work, including inventory sync, order routing, payment reconciliation, and marketplace catalog operations.',
    },
  ],
  primaryCta: { to: '/getintouch', label: 'Discuss an ecommerce project' },
  secondaryCta: { to: '/services', label: 'Explore all services' },
};

export const MARKETPLACE_CATALOGING_PAGE = {
  seo: getPageSeo('/marketplace-cataloging'),
  eyebrow: 'Marketplace cataloging',
  title: 'Marketplace-ready product catalogs from raw data to live listings',
  lead:
    'We help brands, sellers, distributors, and ecommerce teams prepare accurate, searchable, and conversion-focused catalogs for Amazon, Flipkart, Meesho, Myntra, Ajio, JioMart, and more.',
  badges: ['Amazon', 'Flipkart', 'Meesho', 'Myntra', 'Ajio', 'JioMart'],
  services: [
    {
      title: 'Catalog data preparation',
      desc: 'Clean product sheets, normalize SKUs, structure variations, and prepare category-wise attributes.',
    },
    {
      title: 'Template mapping',
      desc: 'Map product data into channel-specific upload templates and marketplace rules.',
    },
    {
      title: 'Search optimized content',
      desc: 'Write keyword-aligned titles, bullet points, descriptions, and backend search fields.',
    },
    {
      title: 'Upload support & fixes',
      desc: 'Bulk uploads, listing corrections, suppressed listing fixes, and rejection resolution.',
    },
  ],
  process: [
    { title: 'Product data', desc: 'Collect SKU sheets, images, specs, and pricing inputs.' },
    { title: 'Content build', desc: 'Create titles, bullets, descriptions, and attribute mappings.' },
    { title: 'QC and upload', desc: 'Validate content, images, data, and launch rules before upload.' },
    { title: 'Live optimization', desc: 'Fix issues and improve visibility after listings go live.' },
  ],
  deliverables: [
    'Title and bullet optimization',
    'Category and attribute mapping',
    'Variation and SKU structuring',
    'Image and spec sheet review',
    'Bulk upload preparation',
    'Listing error resolution',
  ],
  relatedLinks: [
    { to: '/ecommerce-development', label: 'Ecommerce development' },
    { to: '/erp-development', label: 'Commerce ERP' },
    { to: '/getintouch', label: 'Contact' },
  ],
  faqs: [
    {
      question: 'Which marketplaces do you support?',
      answer:
        'We support Amazon, Flipkart, Meesho, Myntra, Ajio, JioMart, and other channel-specific catalog workflows.',
    },
    {
      question: 'Can you continue optimizing listings after launch?',
      answer:
        'Yes. We handle price updates, stock corrections, image changes, SEO updates, and listing issue resolution after go-live.',
    },
    {
      question: 'Do you work from messy supplier data?',
      answer:
        'Yes. We normalize SKU sheets, fix attribute gaps, structure variations, and prepare channel-ready upload templates from incomplete source files.',
    },
    {
      question: 'Can cataloging connect with your ecommerce or ERP work?',
      answer:
        'Yes. Cataloging often sits alongside ecommerce development and commerce ERP projects when teams need listing execution plus operational systems.',
    },
  ],
  primaryCta: { to: '/getintouch', label: 'Start cataloging work' },
};

export const EDUCATION_WEBSITE_PAGE = {
  seo: getPageSeo('/education-website-development'),
  eyebrow: 'Education platform services',
  title: 'Education websites and course discovery platforms for institutions',
  lead:
    'We design and deliver WordPress/Sage platforms with flexible content sections, course and campus pages, admission campaigns, event funnels, search, forms, and ongoing support for global education teams.',
  badges: ['Education CMS', 'Course discovery', 'Campaign pages', 'Forms & CRM'],
  services: [
    {
      title: 'Sage and WordPress engineering',
      desc: 'Custom education themes with Blade, Acorn, Vite, and maintainable frontend tooling.',
    },
    {
      title: 'Course and campus systems',
      desc: 'Structured templates for courses, campuses, programs, and institution content.',
    },
    {
      title: 'Campaign and event funnels',
      desc: 'Landing pages, form heroes, expo flows, and admission campaign experiences.',
    },
    {
      title: 'Search and integrations',
      desc: 'Course discovery, filters, analytics, CRM-ready forms, and embed integrations.',
    },
  ],
  process: [
    { title: 'Plan', desc: 'Align goals, audiences, content models, and rollout priorities.' },
    { title: 'Design', desc: 'Shape templates, navigation, and conversion paths.' },
    { title: 'Build', desc: 'Implement themes, sections, and reusable content blocks.' },
    { title: 'Support', desc: 'Maintain performance, updates, and new publishing needs.' },
  ],
  deliverables: [
    'Higher education websites',
    'Course catalogs',
    'Admissions campaigns',
    'Event funnels',
    'Search and filters',
    'Ongoing CMS support',
  ],
  relatedLinks: [
    { to: '/wordpress-development', label: 'WordPress development' },
    { to: '/website-maintenance', label: 'Website maintenance' },
    { to: '/portfolio', label: 'Portfolio' },
  ],
  faqs: [
    {
      question: 'Can you build flexible page sections for marketing teams?',
      answer:
        'Yes. We implement ACF-driven section builders so non-technical teams can publish heroes, tiles, CTAs, and content bands safely.',
    },
    {
      question: 'Do you support course search and discovery features?',
      answer:
        'Yes. We build filters, search experiences, and structured content models for courses, campuses, and events.',
    },
    {
      question: 'Can you support admissions campaigns and event funnels?',
      answer:
        'Yes. We create landing pages, form heroes, expo flows, and campaign experiences tied to your CMS and analytics stack.',
    },
    {
      question: 'Do you maintain education websites after launch?',
      answer:
        'Yes. We provide ongoing CMS support, performance tuning, new section development, and content platform improvements.',
    },
  ],
  primaryCta: { to: '/getintouch', label: 'Discuss an education platform' },
};

export const FORM_CRM_INTEGRATIONS_PAGE = {
  seo: getPageSeo('/form-crm-integrations'),
  eyebrow: 'Forms and CRM integrations',
  title: 'Lead capture systems connected to your CRM and campaigns',
  lead:
    'We build and maintain multi-provider form stacks with FormAssembly, Marketo, WPForms, Calendly, HubSpot, Salesforce, custom embeds, thank-you pages, and analytics events so enquiries reach the right team cleanly.',
  badges: ['FormAssembly', 'Marketo', 'WPForms', 'HubSpot', 'Salesforce'],
  services: [
    {
      title: 'Multi-provider form architecture',
      desc: 'Unify FormAssembly, Marketo, WPForms, Calendly, custom HTML, and embedded forms behind maintainable CMS controls.',
    },
    {
      title: 'CRM and marketing sync',
      desc: 'Connect leads, orders, campaign fields, and consent data to HubSpot, Salesforce, Marketo, or custom APIs.',
    },
    {
      title: 'Campaign form UX',
      desc: 'Build form heroes, multi-step flows, expo forms, event funnels, thank-you redirects, and validation states.',
    },
    {
      title: 'Tracking and QA',
      desc: 'Add dataLayer events, submission testing, spam controls, field mapping checks, and lead-delivery monitoring.',
    },
  ],
  process: [
    { title: 'Audit', desc: 'Review current forms, CRM fields, campaign flows, and failure points.' },
    { title: 'Map', desc: 'Define field mappings, redirects, tracking events, and owner handoffs.' },
    { title: 'Build', desc: 'Implement forms, embeds, CRM sync, validation, and conversion events.' },
    { title: 'Verify', desc: 'Test submissions, analytics, notifications, and support workflows before launch.' },
  ],
  deliverables: [
    'FormAssembly and Marketo embeds',
    'WPForms and Calendly setup',
    'HubSpot and Salesforce mapping',
    'Thank-you page tracking',
    'Validation and spam controls',
    'Submission QA checklist',
  ],
  relatedLinks: [
    { to: '/education-website-development', label: 'Education websites' },
    { to: '/analytics-tag-management', label: 'Analytics setup' },
    { to: '/woocommerce-payment-crm-integrations', label: 'WooCommerce CRM' },
  ],
  faqs: [
    {
      question: 'Can you support FormAssembly and Marketo on the same website?',
      answer:
        'Yes. We can support multiple form providers on the same CMS and normalize field mapping, redirects, styling, and tracking.',
    },
    {
      question: 'Can forms send leads into HubSpot or Salesforce?',
      answer:
        'Yes. We map fields, owners, lifecycle data, campaign values, and submission events into HubSpot, Salesforce, Marketo, or custom APIs.',
    },
    {
      question: 'Do you handle conversion tracking after form submit?',
      answer:
        'Yes. We add thank-you page events, dataLayer pushes, analytics checks, and QA so marketing teams can measure leads correctly.',
    },
    {
      question: 'Can you improve existing broken forms?',
      answer:
        'Yes. We audit validation, styling, notifications, spam controls, CRM mapping, and hidden campaign fields before rebuilding only what is necessary.',
    },
  ],
  primaryCta: { to: '/getintouch', label: 'Plan form integrations' },
};

export const EDUCATION_SEARCH_PLATFORMS_PAGE = {
  seo: getPageSeo('/education-search-platforms'),
  eyebrow: 'Education search platforms',
  title: 'Course discovery and site search for education websites',
  lead:
    'We build search experiences for education websites: course filters, campus discovery, event search, people/news indexes, autocomplete, and custom indexing rules using Algolia, SiteSearch360, Fuse.js, and WordPress content models.',
  badges: ['Algolia', 'SiteSearch360', 'Course filters', 'Autocomplete'],
  services: [
    {
      title: 'Course and campus search',
      desc: 'Create searchable content models for courses, campuses, programs, events, people, and news.',
    },
    {
      title: 'Faceted filters',
      desc: 'Build filters for study area, location, delivery mode, qualification, intake, and audience.',
    },
    {
      title: 'Hosted search integrations',
      desc: 'Configure Algolia or SiteSearch360 indexes, templates, snippets, autocomplete, and no-result states.',
    },
    {
      title: 'Search analytics and tuning',
      desc: 'Review popular queries, zero-result searches, ranking rules, synonyms, and index hygiene.',
    },
  ],
  process: [
    { title: 'Model', desc: 'Define searchable entities, attributes, filters, and ranking goals.' },
    { title: 'Index', desc: 'Configure hosted search, WordPress hooks, and content exclusion rules.' },
    { title: 'Design', desc: 'Build responsive search UI, autocomplete, snippets, and accessible filters.' },
    { title: 'Improve', desc: 'Tune synonyms, ranking, no-result states, and analytics after launch.' },
  ],
  deliverables: [
    'Course search UI',
    'Faceted filters',
    'Algolia or SiteSearch360 setup',
    'Autocomplete templates',
    'Index configuration',
    'Search analytics review',
  ],
  relatedLinks: [
    { to: '/education-website-development', label: 'Education websites' },
    { to: '/wordpress-development', label: 'WordPress development' },
    { to: '/form-crm-integrations', label: 'Forms and CRM' },
  ],
  faqs: [
    {
      question: 'Do you work with both Algolia and SiteSearch360?',
      answer:
        'Yes. We have worked with Algolia and SiteSearch360 patterns, including WordPress indexing, autocomplete, filters, and search-result templates.',
    },
    {
      question: 'Can you build course search for WordPress education sites?',
      answer:
        'Yes. We model courses and related content in WordPress, configure indexing, and create filters that match admissions and marketing journeys.',
    },
    {
      question: 'Can search include events, people, news, and campuses?',
      answer:
        'Yes. Search can cover multiple content types with custom attributes and templates for each result type.',
    },
    {
      question: 'Do you optimize search after launch?',
      answer:
        'Yes. We tune ranking, synonyms, zero-result handling, and content hygiene based on real search behavior.',
    },
  ],
  primaryCta: { to: '/getintouch', label: 'Plan course search' },
};

export const WOOCOMMERCE_PAYMENT_CRM_PAGE = {
  seo: getPageSeo('/woocommerce-payment-crm-integrations'),
  eyebrow: 'WooCommerce integrations',
  title: 'WooCommerce payment, CRM, and checkout integrations',
  lead:
    'We extend WooCommerce for education and commerce operations with custom checkout UX, Flywire and invoice gateways, HubSpot and Salesforce sync, discount rules, product bundles, webhooks, and PDF invoice workflows.',
  badges: ['WooCommerce', 'Flywire', 'HubSpot', 'Salesforce', 'PDF invoices'],
  services: [
    {
      title: 'Custom checkout UX',
      desc: 'Adapt checkout fields, validation, billing flows, portal codes, and payment choices to match business rules.',
    },
    {
      title: 'Payment gateway integration',
      desc: 'Implement and harden Flywire, invoice payments, PayPal, Stripe-ready flows, and custom webhook handling.',
    },
    {
      title: 'CRM and order sync',
      desc: 'Connect WooCommerce contacts, products, deals, and orders to HubSpot, Salesforce, or custom systems.',
    },
    {
      title: 'Commerce operations',
      desc: 'Support bundles, discounts, PDF invoices, abandoned-cart workflows, reporting, and ongoing fixes.',
    },
  ],
  process: [
    { title: 'Review', desc: 'Audit checkout, gateways, CRM sync, order states, and customer journeys.' },
    { title: 'Design', desc: 'Map payment flows, webhook states, CRM objects, and operational reports.' },
    { title: 'Build', desc: 'Implement checkout changes, gateways, sync jobs, invoices, and admin controls.' },
    { title: 'Support', desc: 'Monitor transactions, fix edge cases, and evolve workflows after launch.' },
  ],
  deliverables: [
    'Custom WooCommerce checkout',
    'Flywire and invoice payment setup',
    'HubSpot and Salesforce sync',
    'Webhook handling',
    'PDF invoice workflows',
    'Discount and bundle rules',
  ],
  relatedLinks: [
    { to: '/ecommerce-development', label: 'Ecommerce development' },
    { to: '/erp-development', label: 'Commerce ERP' },
    { to: '/form-crm-integrations', label: 'Forms and CRM' },
  ],
  faqs: [
    {
      question: 'Can you customize WooCommerce checkout fields and payment rules?',
      answer:
        'Yes. We customize checkout fields, validation, gateway visibility, billing data, and order flows around business rules.',
    },
    {
      question: 'Can WooCommerce sync with HubSpot or Salesforce?',
      answer:
        'Yes. We can sync contacts, deals, products, orders, and abandoned-cart data into CRM systems.',
    },
    {
      question: 'Do you build Flywire or invoice payment flows?',
      answer:
        'Yes. We can implement Flywire-style payment flows, invoice gateways, webhook handling, and transaction-state logic.',
    },
    {
      question: 'Can you maintain an existing WooCommerce integration?',
      answer:
        'Yes. We audit existing plugins and custom code, stabilize checkout issues, and improve reporting and support workflows.',
    },
  ],
  primaryCta: { to: '/getintouch', label: 'Discuss WooCommerce integrations' },
};

export const ANALYTICS_TAG_MANAGEMENT_PAGE = {
  seo: getPageSeo('/analytics-tag-management'),
  eyebrow: 'Analytics and tag management',
  title: 'Marketing measurement that survives real websites',
  lead:
    'We implement analytics and tag governance for complex WordPress and ecommerce sites: GTM, dataLayer events, Hotjar, Clarity, Seeka, LOCALiQ, Crazy Egg, OptinMonster, Poptin, and consent-aware script placement.',
  badges: ['GTM', 'dataLayer', 'Hotjar', 'Clarity', 'Conversion QA'],
  services: [
    {
      title: 'GTM and dataLayer setup',
      desc: 'Define event names, payloads, thank-you tracking, ecommerce events, and tag firing rules.',
    },
    {
      title: 'Heatmaps and behavior tools',
      desc: 'Install and QA Hotjar, Microsoft Clarity, Crazy Egg, Poptin, OptinMonster, and similar tools.',
    },
    {
      title: 'Snippet governance',
      desc: 'Organize head/body/footer scripts through CMS options, HFCM/WPCode, or theme-controlled slots.',
    },
    {
      title: 'Performance and consent checks',
      desc: 'Defer non-critical scripts, manage preconnects, and align tags with privacy and consent requirements.',
    },
  ],
  process: [
    { title: 'Inventory', desc: 'List tags, owners, triggers, consent needs, and duplicate scripts.' },
    { title: 'Plan', desc: 'Define the measurement map, events, script locations, and QA checklist.' },
    { title: 'Implement', desc: 'Add tags, dataLayer pushes, CMS controls, and delayed-loading patterns.' },
    { title: 'Validate', desc: 'Test events, reports, performance impact, and handoff documentation.' },
  ],
  deliverables: [
    'GTM and dataLayer implementation',
    'Form and ecommerce conversion events',
    'Hotjar and Clarity setup',
    'Tag audit and cleanup',
    'Consent-aware script placement',
    'QA and reporting checks',
  ],
  relatedLinks: [
    { to: '/form-crm-integrations', label: 'Forms and CRM' },
    { to: '/website-maintenance', label: 'Website maintenance' },
    { to: '/privacy-translation-widgets', label: 'Privacy and widgets' },
  ],
  faqs: [
    {
      question: 'Can you set up form submit tracking?',
      answer:
        'Yes. We track form submissions through thank-you pages, dataLayer events, provider callbacks, and analytics QA.',
    },
    {
      question: 'Can you clean up messy tracking scripts?',
      answer:
        'Yes. We audit duplicate snippets, unused tags, plugin-injected code, and theme-level scripts before simplifying the setup.',
    },
    {
      question: 'Do you support heatmap and session tools?',
      answer:
        'Yes. We work with Hotjar, Microsoft Clarity, Crazy Egg, Poptin, OptinMonster, and similar marketing tools.',
    },
    {
      question: 'Will tracking hurt site performance?',
      answer:
        'It can if unmanaged. We defer non-critical scripts, add preconnects where useful, and keep critical journeys fast.',
    },
  ],
  primaryCta: { to: '/getintouch', label: 'Plan analytics setup' },
};

export const PRIVACY_TRANSLATION_WIDGETS_PAGE = {
  seo: getPageSeo('/privacy-translation-widgets'),
  eyebrow: 'Privacy, translation and widgets',
  title: 'Trust, consent, translation, and widget integrations for content sites',
  lead:
    'We integrate specialized third-party widgets into WordPress and education websites, including OneTrust privacy notices, GTranslate, Trustpilot, Discover Uni, Unibuddy, Olark, WhatsApp, Google Maps, video, and custom iframes.',
  badges: ['OneTrust', 'GTranslate', 'Trustpilot', 'Unibuddy', 'Google Maps'],
  services: [
    {
      title: 'Privacy and consent widgets',
      desc: 'Embed OneTrust privacy notices, consent scripts, policy pages, and tracking slots without breaking layouts.',
    },
    {
      title: 'Translation and international UX',
      desc: 'Implement GTranslate or multilingual helper widgets with header placement, cookie handling, and mobile polish.',
    },
    {
      title: 'Trust and chat integrations',
      desc: 'Add Trustpilot, Unibuddy, Olark, Meta Messenger, WhatsApp, reviews, and student engagement widgets.',
    },
    {
      title: 'Maps, media, and iframes',
      desc: 'Integrate Google Maps, Discover Uni widgets, YouTube/Vimeo, course catalog iframes, and responsive embed wrappers.',
    },
  ],
  process: [
    { title: 'Scope', desc: 'Confirm widget owners, compliance needs, placement, and performance constraints.' },
    { title: 'Prototype', desc: 'Test embed behavior, responsive layouts, script conflicts, and fallback states.' },
    { title: 'Integrate', desc: 'Add widgets through CMS fields, theme templates, or controlled script slots.' },
    { title: 'Document', desc: 'Hand off editing rules, QA checks, and support notes for future changes.' },
  ],
  deliverables: [
    'OneTrust notice embeds',
    'GTranslate placement',
    'Trustpilot and review widgets',
    'Chat and messaging widgets',
    'Google Maps and media embeds',
    'Responsive iframe wrappers',
  ],
  relatedLinks: [
    { to: '/education-website-development', label: 'Education websites' },
    { to: '/analytics-tag-management', label: 'Analytics setup' },
    { to: '/wordpress-development', label: 'WordPress development' },
  ],
  faqs: [
    {
      question: 'Can you add OneTrust notices to WordPress pages?',
      answer:
        'Yes. We can embed OneTrust notices and privacy content through CMS-controlled fields or theme templates.',
    },
    {
      question: 'Can you improve GTranslate display on mobile?',
      answer:
        'Yes. We can adjust placement, dropdown behavior, cookies, and responsive styles so translation widgets fit the site.',
    },
    {
      question: 'Do you support education-specific widgets?',
      answer:
        'Yes. We can integrate Discover Uni, Unibuddy, course catalog iframes, maps, videos, and student engagement widgets.',
    },
    {
      question: 'Can widgets be added without slowing down pages?',
      answer:
        'Usually yes. We test loading behavior, defer scripts where possible, and isolate embeds from critical content.',
    },
  ],
  primaryCta: { to: '/getintouch', label: 'Plan widget integrations' },
};

export const ERP_DEVELOPMENT_PAGE = {
  seo: getPageSeo('/erp-development'),
  eyebrow: 'Commerce ERP services',
  title: 'Custom ERP systems your operations team can run with confidence',
  lead:
    'We implement custom ERP solutions for ecommerce sellers and operations teams: seller and admin portals, order and inventory automation, multi-channel integrations, reconciliation, accounting exports, and analytics.',
  badges: ['Custom development', 'Multi-channel', 'Inventory', 'Finance'],
  services: [
    {
      title: 'Seller and admin portals',
      desc: 'Role-based panels with catalog tools, order views, approvals, and notifications.',
    },
    {
      title: 'Multi-channel integration',
      desc: 'Connect marketplace, webstore, offline, dealer, and distribution channels into one stack.',
    },
    {
      title: 'Inventory and orders',
      desc: 'Centralize stock, automate bulk orders, fulfillment, returns, and exception handling.',
    },
    {
      title: 'Finance and analytics',
      desc: 'Payment reconciliation, claim-ready reporting, accounting exports, and margin insights.',
    },
  ],
  process: [
    { title: 'Assess', desc: 'Map channels, workflows, data sources, and operational bottlenecks.' },
    { title: 'Architect', desc: 'Design modules, integrations, roles, and reporting structure.' },
    { title: 'Implement', desc: 'Build portals, automation, and connected operational systems.' },
    { title: 'Support', desc: 'Maintain integrations, alerts, and evolving business workflows.' },
  ],
  deliverables: [
    'Seller and admin portals',
    'Multi-channel integrations',
    'Inventory modules',
    'Order automation',
    'Reconciliation tooling',
    'Profit and analytics dashboards',
  ],
  relatedLinks: [
    { to: '/ecommerce-development', label: 'Ecommerce development' },
    { to: '/marketplace-cataloging', label: 'Marketplace cataloging' },
    { to: '/ai-automation', label: 'AI automation' },
  ],
  faqs: [
    {
      question: 'Do you sell an off-the-shelf ERP product?',
      answer:
        'No. We are a service provider and implement custom ERP solutions tailored to how your business actually operates.',
    },
    {
      question: 'Can you integrate marketplaces and finance systems?',
      answer:
        'Yes. We connect marketplaces, webstores, warehouses, shipping, payments, and accounting exports into unified operational workflows.',
    },
    {
      question: 'What modules do commerce teams usually need first?',
      answer:
        'Most projects start with seller/admin portals, order automation, inventory sync, reconciliation, and reporting before expanding into advanced analytics.',
    },
    {
      question: 'Can you extend ERP workflows over time?',
      answer:
        'Yes. We maintain integrations, alerts, permissions, and new modules as channels, teams, and reporting needs evolve.',
    },
  ],
  primaryCta: { to: '/getintouch', label: 'Discuss your ERP project' },
};

export const MOBILE_APP_PAGE = {
  seo: getPageSeo('/mobile-app'),
  eyebrow: 'Mobile app development',
  title: 'Cross-platform mobile apps built for real business workflows',
  lead:
    'We design and deliver React Native apps for iOS and Android—customer experiences, delivery and field tools, and connected operations—with clean APIs, push notifications, store releases, and support after launch.',
  badges: ['React Native', 'iOS & Android', 'Push & deep links', 'API-connected'],
  services: [
    {
      title: 'React Native product apps',
      desc: 'Cross-platform iOS and Android apps with shared codebases, native modules where needed, and release-ready architecture.',
    },
    {
      title: 'Customer & commerce apps',
      desc: 'Catalog browsing, accounts, orders, payments, loyalty, and support flows connected to your web and ecommerce stack.',
    },
    {
      title: 'Field, delivery & ops tools',
      desc: 'Mobile workflows for teams on the ground: job status, scanning, offline-friendly flows, and admin sync.',
    },
    {
      title: 'Integrations & notifications',
      desc: 'REST/GraphQL APIs, CRM and ERP hooks, authentication, push notifications, deep links, and analytics.',
    },
    {
      title: 'Store publishing & CI/CD',
      desc: 'App Store and Google Play setup, build pipelines, versioning, crash monitoring, and release checklists.',
    },
    {
      title: 'Maintenance & iteration',
      desc: 'OS updates, dependency upgrades, performance fixes, feature releases, and store policy compliance over time.',
    },
  ],
  process: [
    {
      title: 'Discover',
      desc: 'Clarify users, platforms, integrations, compliance needs, and the MVP that creates measurable value first.',
    },
    {
      title: 'Design',
      desc: 'Map journeys, information architecture, UI patterns, and API contracts so engineering and design stay aligned.',
    },
    {
      title: 'Build',
      desc: 'Implement React Native screens, native bridges, backend connections, testing, and staging builds.',
    },
    {
      title: 'Launch & improve',
      desc: 'Publish to stores, monitor crashes and usage, then iterate on features, performance, and retention.',
    },
  ],
  deliverables: [
    'React Native iOS & Android apps',
    'API and auth integration',
    'Push notifications & deep links',
    'App Store & Play Console setup',
    'CI/CD release pipeline',
    'Post-launch support plan',
  ],
  relatedLinks: [
    { to: '/web-development', label: 'Web development' },
    { to: '/ecommerce-development', label: 'Ecommerce development' },
    { to: '/ai-automation', label: 'AI automation' },
    { to: '/getintouch', label: 'Contact' },
  ],
  faqs: [
    {
      question: 'Do you build native apps or React Native?',
      answer:
        'We primarily deliver React Native for shared iOS and Android codebases. We add native modules when a feature needs platform-specific performance or capabilities.',
    },
    {
      question: 'Can the app connect to our existing website, CRM, or ecommerce platform?',
      answer:
        'Yes. We commonly integrate with REST/GraphQL APIs, Magento/WooCommerce backends, CRM systems, auth providers, payments, and internal admin tools.',
    },
    {
      question: 'Do you help with App Store and Google Play publishing?',
      answer:
        'Yes. We support certificates, listings, screenshots guidance, build submissions, review feedback handling, and staged rollouts.',
    },
    {
      question: 'What happens after launch?',
      answer:
        'We can maintain OS compatibility, dependency updates, crash fixes, feature releases, and analytics-driven improvements on a support schedule that fits your roadmap.',
    },
  ],
  primaryCta: { to: '/getintouch', label: 'Discuss a mobile app project' },
  secondaryCta: { to: '/services', label: 'Explore all services' },
};
